import { useCallback, useEffect, useState } from 'react';

/** Minimal history router: `/new`, `/follow-ups`, `/table`, `/log`, `/profile`, `/admin` (§6). */
export type View = 'new' | 'follow-ups' | 'table' | 'log' | 'profile' | 'admin';

export const VIEW_PATHS: Record<View, string> = {
  new: '/new',
  'follow-ups': '/follow-ups',
  table: '/table',
  log: '/log',
  profile: '/profile',
  admin: '/admin',
};

export const isPhone = (): boolean => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;

export function viewFromPath(path: string): View {
  const seg = path.split('/')[1] ?? '';
  const hit = (Object.entries(VIEW_PATHS) as [View, string][]).find(([, p]) => p === `/${seg}`);
  // §6.2: the app opens on Follow-ups; on a phone it opens on the New contact form (D-37: that is what a phone is for).
  return hit?.[0] ?? (isPhone() ? 'new' : 'follow-ups');
}

interface Location {
  path: string;
  search: URLSearchParams;
}

function read(): Location {
  return { path: window.location.pathname, search: new URLSearchParams(window.location.search) };
}

const listeners = new Set<() => void>();
function notify() {
  for (const l of listeners) l();
}

export function navigate(path: string, search?: URLSearchParams, replace = false): void {
  const qs = search?.toString();
  const url = qs ? `${path}?${qs}` : path;
  if (url === window.location.pathname + window.location.search) return;
  if (replace) window.history.replaceState(null, '', url);
  else window.history.pushState(null, '', url);
  notify();
}

export function useLocation(): Location {
  const [loc, setLoc] = useState<Location>(read);
  useEffect(() => {
    const update = () => setLoc(read());
    listeners.add(update);
    window.addEventListener('popstate', update);
    return () => {
      listeners.delete(update);
      window.removeEventListener('popstate', update);
    };
  }, []);
  return loc;
}

export function useView(): [View, (v: View) => void] {
  const loc = useLocation();
  const go = useCallback((v: View) => navigate(VIEW_PATHS[v]), []);
  return [viewFromPath(loc.path), go];
}
